import { useIsMobile } from "@lib/hooks/useIsMobile";
import { useEffect, useRef, useState } from "react";
import { SectionHeader } from "./SectionHeader";
import type { Theme } from "./theme";

type SlideVariant = "complexity" | "split" | "focus" | "blocks" | "journey";

type Slide = {
  id: SlideVariant;
  headline: string[];
  support: string;
};

const SLIDES: Slide[] = [
  {
    id: "complexity",
    headline: ["Recruitment has become increasingly complex.", "Outcomes are still inconsistent."],
    support:
      "Multiple steps, systems and stakeholders — without a consistent way to make decisions.",
  },
  {
    id: "split",
    headline: ["More process doesn't always lead to better hiring decisions."],
    support: "Different perspectives, shifting criteria, and subjective judgement all play a role.",
  },
  {
    id: "focus",
    headline: [
      "Successful recruitment depends on many factors.",
      "But two consistently shape the quality of hire.",
    ],
    support: "These are the moments where alignment matters most.",
  },
  {
    id: "blocks",
    headline: [
      "Clarity on what success looks like.",
      "Consistency in how candidates are assessed.",
    ],
    support: "Both rely on structured collaboration across hiring teams.",
  },
  {
    id: "journey",
    headline: ["Better alignment.", "Stronger hiring decisions.", "Reduced reliance on agencies."],
    support: "A more structured, collaborative approach to hiring.",
  },
];

export function FeatureCarousel({ theme }: { theme: Theme }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const slideWidth = track.clientWidth;
      if (slideWidth === 0) return;
      const idx = Math.round(track.scrollLeft / slideWidth);
      setActive(Math.max(0, Math.min(SLIDES.length - 1, idx)));
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (idx: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: idx * track.clientWidth, behavior: "smooth" });
  };

  return (
    <section
      style={{
        padding: "clamp(72px, 12vw, 120px) 0",
        borderTop: `1px solid ${theme.border}`,
      }}
    >
      <SectionHeader
        theme={theme}
        kicker="The shift"
        title="From process complexity to decision clarity."
      />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 clamp(20px, 5vw, 40px)" }}>
        <div
          ref={trackRef}
          role="region"
          aria-roledescription="carousel"
          aria-label="Recruitment story"
          style={{
            display: "grid",
            gridAutoFlow: "column",
            gridAutoColumns: "100%",
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            scrollBehavior: "smooth",
            border: `1px solid ${theme.border}`,
            borderRadius: theme.radius * 1.5,
            background: theme.surface,
            scrollbarWidth: "none",
          }}
        >
          {SLIDES.map((slide, i) => (
            <div
              key={slide.id}
              role="group"
              aria-roledescription="slide"
              aria-label={`Slide ${i + 1} of ${SLIDES.length}`}
              style={{
                scrollSnapAlign: "start",
                padding: isMobile ? "32px 24px" : "56px 48px",
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1.05fr 1fr",
                gap: isMobile ? 28 : 48,
                alignItems: "center",
                minHeight: isMobile ? 0 : 460,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: theme.mono,
                    fontSize: 12,
                    letterSpacing: 0.8,
                    color: theme.accentFg,
                    marginBottom: 20,
                  }}
                >
                  0{i + 1} / 0{SLIDES.length}
                </div>
                {slide.headline.map((line, j) => (
                  <div
                    key={`${slide.id}-${line}`}
                    style={{
                      fontSize: "clamp(22px, 4vw, 32px)",
                      fontWeight: 600,
                      color: theme.fg,
                      letterSpacing: -0.8,
                      lineHeight: 1.2,
                      marginBottom: j < slide.headline.length - 1 ? 8 : 24,
                    }}
                  >
                    {line}
                  </div>
                ))}
                <p
                  style={{
                    fontSize: 17,
                    color: theme.fgMute,
                    lineHeight: 1.6,
                    maxWidth: 520,
                    marginTop: 24,
                    paddingTop: 24,
                    borderTop: `1px solid ${theme.border}`,
                  }}
                >
                  {slide.support}
                </p>
              </div>
              <SlideVisual variant={slide.id} theme={theme} isMobile={isMobile} />
            </div>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            marginTop: 32,
          }}
        >
          {SLIDES.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === active}
              onClick={() => goTo(i)}
              style={{
                width: i === active ? 32 : 8,
                height: 8,
                borderRadius: 4,
                background: i === active ? theme.accent : theme.border,
                border: "none",
                padding: 0,
                cursor: "pointer",
                transition: "all .25s ease",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function SlideVisual({
  variant,
  theme,
  isMobile,
}: {
  variant: SlideVariant;
  theme: Theme;
  isMobile: boolean;
}) {
  return (
    <div
      style={{
        position: "relative",
        height: isMobile ? 200 : 320,
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderRadius: theme.radius * 1.5,
        overflow: "hidden",
      }}
    >
      {variant === "complexity" && <ComplexityVisual theme={theme} />}
      {variant === "split" && <SplitVisual theme={theme} />}
      {variant === "focus" && <FocusVisual theme={theme} />}
      {variant === "blocks" && <BlocksVisual theme={theme} />}
      {variant === "journey" && <JourneyVisual theme={theme} />}
    </div>
  );
}

const RECTS = [
  { id: "a", x: 8, y: 12, w: 38, h: 14, r: -8 },
  { id: "b", x: 24, y: 28, w: 30, h: 12, r: 6 },
  { id: "c", x: 48, y: 18, w: 36, h: 14, r: -3 },
  { id: "d", x: 14, y: 48, w: 42, h: 16, r: 4 },
  { id: "e", x: 50, y: 44, w: 28, h: 14, r: -10 },
  { id: "f", x: 32, y: 64, w: 34, h: 12, r: 2 },
  { id: "g", x: 60, y: 70, w: 26, h: 14, r: 12 },
];

function ComplexityVisual({ theme }: { theme: Theme }) {
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ width: "100%", height: "100%" }}
      role="presentation"
      aria-hidden="true"
    >
      <title>Layered process diagram</title>
      {RECTS.map((r) => (
        <g key={r.id} transform={`rotate(${r.r} ${r.x + r.w / 2} ${r.y + r.h / 2})`}>
          <rect
            x={r.x}
            y={r.y}
            width={r.w}
            height={r.h}
            rx={2}
            fill={theme.surface}
            stroke={theme.border}
            strokeWidth={0.4}
          />
        </g>
      ))}
    </svg>
  );
}

const PROCESS_BARS = [0.92, 0.78, 0.85, 0.71, 0.88, 0.65];
const OUTCOME_BARS = [0.6, 0.32, 0.78, 0.45, 0.55, 0.28];

function SplitVisual({ theme }: { theme: Theme }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", height: "100%" }}>
      <div style={{ borderRight: `1px solid ${theme.border}`, padding: 24, position: "relative" }}>
        <div
          style={{
            fontFamily: theme.mono,
            fontSize: 11,
            color: theme.fgMute,
            letterSpacing: 0.6,
            marginBottom: 16,
          }}
        >
          PROCESS
        </div>
        {PROCESS_BARS.map((w) => (
          <div
            key={`process-${w}`}
            style={{
              height: 6,
              width: `${w * 100}%`,
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: 3,
              marginBottom: 8,
            }}
          />
        ))}
      </div>
      <div style={{ padding: 24 }}>
        <div
          style={{
            fontFamily: theme.mono,
            fontSize: 11,
            color: theme.fgMute,
            letterSpacing: 0.6,
            marginBottom: 16,
          }}
        >
          OUTCOMES
        </div>
        {OUTCOME_BARS.map((w) => (
          <div
            key={`outcome-${w}`}
            style={{
              height: 6,
              width: `${w * 100}%`,
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: 3,
              marginBottom: 8,
            }}
          />
        ))}
      </div>
    </div>
  );
}

const FOCUS_DOTS = Array.from({ length: 28 }, (_, i) => {
  const seed = (i * 9301 + 49297) % 233280;
  return {
    id: `dot-${i}`,
    x: 10 + (((seed * 7) % 100) / 100) * 80,
    y: 10 + (((seed * 13) % 100) / 100) * 80,
  };
});

function FocusVisual({ theme }: { theme: Theme }) {
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ width: "100%", height: "100%" }}
      role="presentation"
      aria-hidden="true"
    >
      <title>Two factors in focus among many</title>
      {FOCUS_DOTS.map((d) => (
        <circle key={d.id} cx={d.x} cy={d.y} r={1.4} fill={theme.border} opacity={0.6} />
      ))}
      <circle
        cx={36}
        cy={50}
        r={7}
        fill={theme.accentSoft}
        stroke={theme.accent}
        strokeWidth={0.8}
      />
      <circle
        cx={64}
        cy={50}
        r={7}
        fill={theme.accentSoft}
        stroke={theme.accent}
        strokeWidth={0.8}
      />
      <circle cx={36} cy={50} r={3} fill={theme.accent} />
      <circle cx={64} cy={50} r={3} fill={theme.accent} />
    </svg>
  );
}

const STAKEHOLDER_YS = [20, 35, 50, 65, 80];

function BlocksVisual({ theme }: { theme: Theme }) {
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ width: "100%", height: "100%" }}
      role="presentation"
      aria-hidden="true"
    >
      <title>Clarity and consistency blocks with stakeholder inputs</title>
      {STAKEHOLDER_YS.map((y) => (
        <circle key={`stake-${y}`} cx={12} cy={y} r={2} fill={theme.fgMute} opacity={0.5} />
      ))}
      {STAKEHOLDER_YS.map((y) => (
        <g key={`line-${y}`}>
          <line x1={14} y1={y} x2={42} y2={36} stroke={theme.border} strokeWidth={0.4} />
          <line x1={14} y1={y} x2={42} y2={64} stroke={theme.border} strokeWidth={0.4} />
        </g>
      ))}
      <rect
        x={42}
        y={26}
        width={44}
        height={20}
        rx={2}
        fill={theme.accentSoft}
        stroke={theme.accent}
        strokeWidth={0.6}
      />
      <rect
        x={42}
        y={54}
        width={44}
        height={20}
        rx={2}
        fill={theme.accentSoft}
        stroke={theme.accent}
        strokeWidth={0.6}
      />
      <line x1={64} y1={46} x2={64} y2={54} stroke={theme.accent} strokeWidth={0.8} />
      <text x={64} y={38} textAnchor="middle" fontSize={5} fill={theme.fg} fontWeight={600}>
        Clarity
      </text>
      <text x={64} y={66} textAnchor="middle" fontSize={5} fill={theme.fg} fontWeight={600}>
        Consistency
      </text>
    </svg>
  );
}

const JOURNEY_NODES = [12, 32, 52, 72, 92];

function JourneyVisual({ theme }: { theme: Theme }) {
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ width: "100%", height: "100%" }}
      role="presentation"
      aria-hidden="true"
    >
      <title>Aligned hiring journey ending at the decision</title>
      <line x1={12} y1={50} x2={92} y2={50} stroke={theme.border} strokeWidth={0.6} />
      {JOURNEY_NODES.map((x, i) => (
        <g key={`node-${x}`}>
          <circle cx={x} cy={28} r={1.6} fill={theme.fgMute} opacity={0.6} />
          <line x1={x} y1={30} x2={x} y2={46} stroke={theme.border} strokeWidth={0.3} />
          <circle
            cx={x}
            cy={50}
            r={4}
            fill={i === JOURNEY_NODES.length - 1 ? theme.accent : theme.bg}
            stroke={theme.accent}
            strokeWidth={0.8}
          />
        </g>
      ))}
      <text x={92} y={72} textAnchor="middle" fontSize={5} fill={theme.accentFg} fontWeight={600}>
        decision
      </text>
    </svg>
  );
}
